import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { API_CONFIG, ApiConfig } from '../../api-config';
import { ApiResponse } from '@shared-models';
import { toCountryTitleApiPayload } from '../../../utils/country-title-catalog.utils';

/**
 * Interfaz genérica que matchea el shape de respuesta paginada del
 * backend. Idéntica a la de `FiscalPersonTypeService`.
 */
interface PageableData {
  content: any[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  first: boolean;
  last: boolean;
  empty: boolean;
}

/**
 * Service para el catálogo de "Clasificaciones" asociado a un país.
 *
 * Endpoints:
 *   GET    /api/settings/countries/{countryId}/type-classifications
 *   POST   /api/settings/countries/{countryId}/type-classifications
 *   PUT    /api/settings/countries/{countryId}/type-classifications/{id}
 *   DELETE /api/settings/countries/{countryId}/type-classifications/{id}
 *
 * NOTA sobre naming: la URL usa `type-classifications` en
 * singular (provista por el backend tal cual), a diferencia del
 * endpoint hermano `type-people` (plural, el de
 * fiscal-person-type). Esto es decisión del backend, NO del front.
 *
 * IMPORTANTE: este service es DISTINTO del set de clasificaciones
 * globales (business-activity, personal-activity, company-types,
 * etc.) que ya existen en `apps/admin-system/src/app/features/
 * structure/countries/components/classification-*`. Esas son
 * catálogos GLOBALES. Este service es de un subtipo de
 * clasificación ESPECÍFICO POR PAÍS.
 *
 * Mismo shape que `FiscalPersonType`: `id, code, title, status,
 * createdAt`. CRUD completo. Paginación server-side.
 */
@Injectable({ providedIn: 'root' })
export class ClassificationTypeService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  private get apiUrl(): string {
    return `${this.apiConfig.apiUrl}/settings/countries`;
  }

  private normalizeStatus(status: string | undefined): 'active' | 'inactive' {
    if (!status) return 'active';
    return status.toLowerCase() as 'active' | 'inactive';
  }

  /**
   * LIST: Clasificaciones de un país (server-side pagination)
   */
  getClassificationTypes(
    countryId: string,
    page = 0,
    size = 20,
    sort = 'createdAt',
    direction: 'asc' | 'desc' = 'desc',
    search?: string,
    status?: string,
  ): Observable<ClassificationTypePageResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('sort', sort)
      .set('direction', direction);

    if (search && search.trim()) {
      params = params.set('search', search.trim());
    }
    if (status && status.trim()) {
      params = params.set('status', status.trim().toUpperCase());
    }

    const url = `${this.apiUrl}/${countryId}/type-classifications`;
    return this.httpClient.get<ApiResponse<PageableData>>(url, { params }).pipe(
      map((response) => {
        const pageableData = response.data;
        const items = pageableData?.content || [];
        return {
          items: items.map((it: any) => ({
            id: it.id,
            code: it.code || '',
            title: it.name || it.title || '',
            status: this.normalizeStatus(it.status),
            createdAt: it.createdAt || it.created_at || '',
          })) as ClassificationType[],
          page: pageableData?.page ?? 0,
          size: pageableData?.size ?? size,
          totalElements: pageableData?.totalElements ?? 0,
          totalPages: pageableData?.totalPages ?? 0,
        } as ClassificationTypePageResponse;
      }),
    );
  }

  getClassificationTypeById(
    countryId: string,
    id: string | number,
  ): Observable<ClassificationType> {
    const url = `${this.apiUrl}/${countryId}/type-classifications/${id}`;
    return this.httpClient.get<ApiResponse<any>>(url).pipe(
      map((response) => {
        const it = response.data;
        return {
          id: it.id,
          code: it.code || '',
          title: it.name || it.title || '',
          status: this.normalizeStatus(it.status),
          createdAt: it.createdAt || it.created_at || '',
        } as ClassificationType;
      }),
    );
  }

  createClassificationType(
    countryId: string,
    payload: any,
  ): Observable<{ message: string }> {
    const url = `${this.apiUrl}/${countryId}/type-classifications`;
    return this.httpClient
      .post<{ success: boolean; message: string }>(
        url,
        toCountryTitleApiPayload(payload),
      )
      .pipe(
        map((res) => ({
          message: res.message || 'Clasificación creada',
        })),
      );
  }

  updateClassificationType(
    countryId: string,
    id: string | number,
    payload: any,
  ): Observable<{ message: string }> {
    const url = `${this.apiUrl}/${countryId}/type-classifications/${id}`;
    return this.httpClient
      .put<{ success: boolean; message: string }>(
        url,
        toCountryTitleApiPayload(payload),
      )
      .pipe(
        map((res) => ({
          message: res.message || 'Clasificación actualizada',
        })),
      );
  }

  deleteClassificationType(
    countryId: string,
    id: string | number,
  ): Observable<{ message: string }> {
    const url = `${this.apiUrl}/${countryId}/type-classifications/${id}`;
    return this.httpClient
      .delete<{ success: boolean; message: string }>(url)
      .pipe(
        map((res) => ({
          message: res.message || 'Clasificación eliminada',
        })),
      );
  }
}

export interface ClassificationType {
  id: string;
  code: string;
  title: string;
  status?: string;
  createdAt?: string;
}

export interface ClassificationTypePageResponse {
  items: ClassificationType[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}
